// useAnalyticsFilters hook
// CREATED: Manages Analytics page filter state with localStorage persistence
// Features:
// - Loads sites from Supabase based on user role
// - Restores site, mission, date range, and time slot from localStorage (7-day TTL)
// - Auto-selects first site on first visit (matching Team page behavior)
// - Auto-selects first available mission when missions load and no valid selection exists
// - Validates restored preferences against current available data
// - Debounced save (500ms) to avoid excessive writes
// - When site changes, resets mission and time selections
// UPDATED: Added selectedTime state with handleTimeChange callback and persistence
// FIXED: getDefaultDateFrom/getDefaultDateTo now use local date (not UTC) to avoid off-by-one at midnight
// FIXED: Added missionRestoreAttempted flag to prevent save race that overwrote lastMissionId with null
// FIXED: Added missionsLoading param; mission restore effect early-returns while data is in flight so the
//        debounced save does not fire with lastMissionId:null before missions have loaded

import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../lib/supabase';
import type { Site } from '../lib/supabase';
import {
  saveAnalyticsPreferences,
  getAnalyticsPreferences,
  AnalyticsPreferences
} from '../lib/analyticsPreferences';
import type { MissionOption } from '../lib/analytics';

interface UseAnalyticsFiltersParams {
  userId: string | null;
  userRole: string | null;
  availableMissions: MissionOption[];
  missionsLoading: boolean;
}

function toLocalDateStr(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function getDefaultDateFrom(): string {
  const from = new Date();
  from.setDate(from.getDate() - 29);
  return toLocalDateStr(from);
}

function getDefaultDateTo(): string {
  return toLocalDateStr(new Date());
}

export function useAnalyticsFilters({
  userId,
  userRole,
  availableMissions,
  missionsLoading
}: UseAnalyticsFiltersParams) {
  const [sites, setSites] = useState<Site[]>([]);
  const [sitesLoading, setSitesLoading] = useState(true);
  const [selectedSiteId, setSelectedSiteId] = useState<string | null>(null);
  const [selectedMissionId, setSelectedMissionId] = useState<string | null>(null);
  const [selectedTime, setSelectedTime] = useState<string | null>(null);
  const [dateFrom, setDateFrom] = useState(getDefaultDateFrom());
  const [dateTo, setDateTo] = useState(getDefaultDateTo());
  const [isInitialized, setIsInitialized] = useState(false);
  const [missionRestoreAttempted, setMissionRestoreAttempted] = useState(false);
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (!userId || !userRole) return;

    const loadSites = async () => {
      setSitesLoading(true);
      try {
        let siteList: Site[] = [];
        if (userRole === 'admin' || userRole === 'director') {
          const { data } = await supabase
            .from('sites')
            .select('*')
            .eq('is_archived', false)
            .order('name');
          siteList = data || [];
        } else {
          const { data } = await supabase
            .from('user_sites')
            .select('sites(*)')
            .eq('user_id', userId);
          siteList = (data || [])
            .map((r: any) => r.sites)
            .filter(Boolean)
            .filter((s: Site) => !s.is_archived);
        }
        setSites(siteList);
      } finally {
        setSitesLoading(false);
      }
    };

    loadSites();
  }, [userId, userRole]);

  useEffect(() => {
    if (!userId || sites.length === 0 || isInitialized) return;

    const savedPrefs = getAnalyticsPreferences(userId);
    if (savedPrefs) {
      const siteIds = sites.map(s => s.id);
      const validSiteId = savedPrefs.lastSiteId && siteIds.includes(savedPrefs.lastSiteId)
        ? savedPrefs.lastSiteId
        : sites[0].id;
      setSelectedSiteId(validSiteId);
      if (savedPrefs.lastDateFrom) setDateFrom(savedPrefs.lastDateFrom);
      if (savedPrefs.lastDateTo) setDateTo(savedPrefs.lastDateTo);
      if (savedPrefs.lastTimeSlot) setSelectedTime(savedPrefs.lastTimeSlot);
    } else {
      setSelectedSiteId(sites[0].id);
    }

    setIsInitialized(true);
  }, [userId, sites, isInitialized]);

  useEffect(() => {
    if (missionsLoading) return;

    if (availableMissions.length === 0) {
      setMissionRestoreAttempted(true);
      return;
    }

    const missionIds = availableMissions.map(m => m.templateId);

    if (selectedMissionId && missionIds.includes(selectedMissionId)) {
      setMissionRestoreAttempted(true);
      return;
    }

    if (userId) {
      const savedPrefs = getAnalyticsPreferences(userId);
      if (savedPrefs?.lastMissionId && missionIds.includes(savedPrefs.lastMissionId)) {
        setSelectedMissionId(savedPrefs.lastMissionId);
        setMissionRestoreAttempted(true);
        return;
      }
    }

    setSelectedMissionId(missionIds[0]);
    setMissionRestoreAttempted(true);
  }, [availableMissions, selectedMissionId, userId, missionsLoading]);

  const debouncedSave = useCallback(() => {
    if (!userId) return;
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    saveTimeoutRef.current = setTimeout(() => {
      const prefs: AnalyticsPreferences = {
        lastSiteId: selectedSiteId,
        lastMissionId: selectedMissionId,
        lastDateFrom: dateFrom,
        lastDateTo: dateTo,
        lastTimeSlot: selectedTime
      };
      saveAnalyticsPreferences(userId, prefs);
    }, 500);
  }, [userId, selectedSiteId, selectedMissionId, dateFrom, dateTo, selectedTime]);

  useEffect(() => {
    if (isInitialized && missionRestoreAttempted && !missionsLoading) debouncedSave();
  }, [selectedSiteId, selectedMissionId, dateFrom, dateTo, selectedTime, isInitialized, missionRestoreAttempted, missionsLoading, debouncedSave]);

  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    };
  }, []);

  const handleSiteChange = useCallback((siteId: string) => {
    setSelectedSiteId(siteId);
    setSelectedMissionId(null);
    setSelectedTime(null);
  }, []);

  const handleMissionChange = useCallback((missionId: string | null) => {
    setSelectedMissionId(missionId);
    setSelectedTime(null);
  }, []);

  const handleTimeChange = useCallback((time: string | null) => {
    setSelectedTime(time);
  }, []);

  const handleDateRangeChange = useCallback((from: string, to: string) => {
    if (from) setDateFrom(from);
    if (to) setDateTo(to);
    setSelectedTime(null);
  }, []);

  return {
    sites,
    sitesLoading,
    selectedSiteId,
    selectedMissionId,
    selectedTime,
    dateFrom,
    dateTo,
    isInitialized,
    handleSiteChange,
    handleMissionChange,
    handleTimeChange,
    handleDateRangeChange
  };
}
